import React, { useState } from 'react';
import { Card, Tabs, Form, Input, Button, Switch, Select, message, Divider } from 'antd';
import { UserOutlined, KeyOutlined, BellOutlined, GlobalOutlined } from '@ant-design/icons';
import { useTheme } from '../context/ThemeContext';
import { useThemeStyles } from '../context/ThemeContext';
import { pageShellStyle } from '../theme';

const Settings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { theme } = useTheme();
  const themeStyles = useThemeStyles();

  const handleSave = async (values) => {
    setLoading(true);
    try {
      message.info('Settings management is coming soon. Your preferences have been noted.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ ...pageShellStyle, maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ 
          margin: 0, 
          fontSize: '28px', 
          fontWeight: '600', 
          color: themeStyles.textPrimary, 
          textShadow: 'none'
        }}>
          Settings
        </h2>
        <div style={{ 
          height: '1px', 
          background: 'linear-gradient(90deg, rgba(148, 22, 127, 0.6) 0%, rgba(21, 60, 180, 0.6) 100%)', 
          marginTop: '12px' 
        }} />
      </div>

      <Card
        style={{ 
          background: 'linear-gradient(135deg, rgba(148, 22, 127, 0.1) 0%, rgba(21, 60, 180, 0.1) 100%)', 
          border: '1px solid rgba(148, 22, 127, 0.3)', borderRadius: '12px',
          boxShadow: themeStyles.shadow
        }}
      >
        <Tabs
          defaultActiveKey="profile"
          items={[
            {
              key: 'profile',
              label: (
                <span>
                  <UserOutlined /> Profile
                </span>
              ),
              children: (
                <Form form={form} layout="vertical" onFinish={handleSave}>
                  <Form.Item 
                    label="Username" 
                    name="username"
                    rules={[{ required: true, message: 'Please enter your username' }]}
                  >
                    <Input placeholder="Enter username" />
                  </Form.Item>
                  <Form.Item 
                    label="Email" 
                    name="email"
                    rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}
                  >
                    <Input placeholder="Enter email" />
                  </Form.Item>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Save Changes
                  </Button>
                </Form>
              )
            },
            {
              key: 'api',
              label: (
                <span>
                  <KeyOutlined /> API Keys
                </span>
              ),
              children: (
                <Form form={form} layout="vertical" onFinish={handleSave}>
                   <div style={{ color: 'rgba(255, 255, 255, 0.45)', fontSize: '12px', marginBottom: '16px' }}>
                    API keys are stored securely and never exposed in responses
                  </div>
                  <Form.Item 
                    label="OpenAI API Key" 
                    name="openai_key"
                    extra="Used for embeddings and LLM responses"
                  >
                    <Input.Password placeholder="sk-..." />
                  </Form.Item>
                  <Form.Item 
                    label="Pinecone API Key" 
                    name="pinecone_key"
                    extra="Required if using Pinecone vector store"
                  >
                    <Input.Password placeholder="Enter Pinecone API key" />
                  </Form.Item>
                 
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Save API Keys
                  </Button>
                </Form>
              )
            },
            {
              key: 'notifications',
              label: (
                <span>
                  <BellOutlined /> Notifications
                </span>
              ),
              children: (
                <Form form={form} layout="vertical" onFinish={handleSave}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                    <Form.Item 
                      label="Email Notifications" 
                      name="email_notifications"
                      valuePropName="checked"
                      extra="Receive email updates about your projects"
                    >
                      <Switch />
                    </Form.Item>
                    <Form.Item 
                      label="Deployment Alerts" 
                      name="deployment_alerts"
                      valuePropName="checked"
                      extra="Get notified when deployments complete or fail"
                    >
                      <Switch />
                    </Form.Item>
                    <Form.Item 
                      label="Guardrail Violations" 
                      name="guardrail_alerts"
                      valuePropName="checked"
                      extra="Alert when guardrails block content"
                    >
                      <Switch />
                    </Form.Item>
                  </div>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Save Preferences
                  </Button>
                </Form>
              )
            },
            {
              key: 'general',
              label: (
                <span>
                  <GlobalOutlined /> General
                </span>
              ),
              children: (
                <Form form={form} layout="vertical" onFinish={handleSave}>
                  <Form.Item 
                    label="Default LLM Model" 
                    name="default_llm"
                    initialValue="gpt-4o-mini"
                  >
                    <Select>
                      <Select.Option value="gpt-4o-mini">GPT-4o Mini</Select.Option>
                      <Select.Option value="gpt-4o">GPT-4o</Select.Option>
                      <Select.Option value="gpt-3.5-turbo">GPT-3.5 Turbo</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item 
                    label="Default Embedding Model" 
                    name="default_embedding"
                    initialValue="text-embedding-3-small"
                  >
                    <Select>
                      <Select.Option value="text-embedding-ada-002">text-embedding-ada-002</Select.Option>
                      <Select.Option value="text-embedding-3-small">text-embedding-3-small</Select.Option>
                      <Select.Option value="text-embedding-3-large">text-embedding-3-large</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item 
                    label="Default Vector Store" 
                    name="default_vector_store"
                    initialValue="milvus"
                  >
                    <Select>
                      <Select.Option value="milvus">Milvus</Select.Option>
                      <Select.Option value="pinecone" disabled>Pinecone - Upcoming</Select.Option>
                    </Select>
                  </Form.Item>
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Save Defaults
                  </Button>
                </Form>
              )
            }
          ]}
        />
      </Card>
    </div>
  );
};

export default Settings;
